import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { map, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { IEventNotificationModel } from '../event-notification.model';
import { EventNotificationsService } from '../event-notifications.service';
import { DropdownModel } from '../../../shared/models/dropdown-model';
import { DateTimeUnitEnum } from '../../enums/datetime-unit.enum';
import { AppState } from '../../../store/models/app.state';
import { loadEventNotificationsListByEventId } from '../../store/event.actions';
import { FormControls } from '../../../shared/types/form-controls.type';
import { UserRoleEnum } from '../../../user/user-roles.enum';
import { TimingOptions } from '../../../shared/models/timing-options.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { LowerCasePipe, NgClass } from '@angular/common';
import { MATERIAL_MODULES } from '../../../shared/shared.providers';
import { TranslatePipe } from '@ngx-translate/core';
import { PharFormFieldComponent } from '../../../shared/phar-form-field/phar-form-field.component';
import { Select } from '@openng/optimus-ui/select';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ErrorValidationComponent } from '../../../form/form-preview/validators/validators.component';
import { PharDropdownTimepicker2Component } from '../../../shared/phar-dropdown-timepicker2/phar-dropdown-timepicker2.component';

export type NotificationFormGroupValue = Omit<
  IEventNotificationModel,
  'eventId' | 'id' | 'shouldNotifyResponsePerson' | 'userIds'
>;
type NotificationFormGroup = FormControls<NotificationFormGroupValue>;

@Component({
  selector: 'phar-event-notification',
  templateUrl: 'event-notification.component.html',
  styleUrls: ['event-notification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    MATERIAL_MODULES,
    ButtonModule,
    TranslatePipe,
    ReactiveFormsModule,
    LowerCasePipe,
    PharFormFieldComponent,
    Select,
    ErrorValidationComponent,
    PharDropdownTimepicker2Component,
  ],
})
export class EventNotificationComponent implements OnInit {
  eventNotification = input.required<IEventNotificationModel>();
  disabled: InputSignal<boolean> = input<boolean>(false);
  draftMode = input<boolean>(false);
  createNewNotification: OutputEmitterRef<IEventNotificationModel> = output<IEventNotificationModel>();
  deleteNewNotification = output();
  formValueChanges = output<{ value: IEventNotificationModel; isValid: boolean }>();
  form: FormGroup<NotificationFormGroup>;
  isOpenState = false;
  injector: Injector = inject(Injector);
  store: Store<AppState> = inject(Store);
  eventNotificationsService: EventNotificationsService = inject(EventNotificationsService);

  isNewNotification = computed(() => typeof this.eventNotification().id !== 'number');
  formValue: Signal<NotificationFormGroupValue>;
  timingControlValue: Signal<TimingOptions | null>;
  hasPendingChanges: Signal<boolean>;

  readonly userRoles: UserRoleEnum[] = [UserRoleEnum.Coordinator, UserRoleEnum.Investigator, UserRoleEnum.Participant];
  readonly timingOptions = [
    {
      value: TimingOptions.Before,
      label: 'STUDY.EVENTS.NOTIFICATIONS.TIMING_OPTIONS.BEFORE',
    },
    {
      value: TimingOptions.During,
      label: 'STUDY.EVENTS.NOTIFICATIONS.TIMING_OPTIONS.DURING',
    },
    {
      value: TimingOptions.After,
      label: 'STUDY.EVENTS.NOTIFICATIONS.TIMING_OPTIONS.AFTER',
    },
  ];
  readonly timeUnitDisplayOptions: DropdownModel[] = [
    {
      value: DateTimeUnitEnum.Day,
      label: 'UNITS.DAY_FULL',
    },
    {
      value: DateTimeUnitEnum.Week,
      label: 'UNITS.WEEK_FULL',
    },
    {
      value: DateTimeUnitEnum.Month,
      label: 'UNITS.MONTH_FULL',
    },
    // {
    //   value: DateTimeUnitEnum.Year, @ТОДО This option is current not available in the other system so it should stay commented for now
    // }
  ];
  readonly userRoleLabels: Record<UserRoleEnum, string> = {
    [UserRoleEnum.Coordinator]: 'STUDY.EVENTS.NOTIFICATIONS.USER_ROLES.COORDINATOR',
    [UserRoleEnum.Investigator]: 'STUDY.EVENTS.NOTIFICATIONS.USER_ROLES.INVESTIGATOR',
    [UserRoleEnum.Participant]: 'STUDY.EVENTS.NOTIFICATIONS.USER_ROLES.PARTICIPANT',
  };

  ngOnInit() {
    this.form = this.getInitialFormGroup();
    this.form.reset(this.eventNotification());
    this.formValue = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
      injector: this.injector,
      initialValue: this.form.getRawValue(),
    }) as Signal<NotificationFormGroupValue>;

    const timingControl = this.form.controls.timing;
    this.timingControlValue = toSignal(timingControl.valueChanges, {
      injector: this.injector,
      initialValue: timingControl.value,
    });
    this.hasPendingChanges = computed(() => {
      const formValue = this.formValue();
      if (this.draftMode()) {
        return this.form.touched && this.form.invalid;
      }

      return !isEqual(
        {
          ...this.eventNotification(),
        },
        {
          ...this.eventNotification(),
          ...formValue,
        },
      );
    });

    if (this.isNewNotification()) {
      this.isOpenState = true;
    }

    if (this.disabled()) {
      this.form.disable();
    }

    this.initListeners();
  }

  private initListeners(): void {
    effect(
      () => {
        if (!this.form) {
          return;
        }

        if (this.disabled()) {
          this.form.disable();
        } else {
          this.form.enable();
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        this.handleTimingChange(this.timingControlValue());
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const fv = this.formValue();
        this.formValueChanges.emit({ value: fv as IEventNotificationModel, isValid: this.form.valid });
      },
      {
        injector: this.injector,
      },
    );
  }

  saveNotification(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const notification: IEventNotificationModel = {
      ...this.eventNotification(),
      ...this.form.getRawValue(),
    };

    if (this.isNewNotification()) {
      this.createNewNotification.emit(notification);
      return;
    }

    this.eventNotificationsService
      .update(notification)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(loadEventNotificationsListByEventId({ id: notification.eventId }));
        },
      });
  }

  deleteNotification(): void {
    if (this.isNewNotification() || this.draftMode()) {
      this.deleteNewNotification.emit();
      return;
    }

    this.eventNotificationsService
      .delete(this.eventNotification().id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(loadEventNotificationsListByEventId({ id: this.eventNotification().eventId }));
        },
      });
  }

  toggleOpenCloseState(): void {
    this.isOpenState = !this.isOpenState;
  }

  discardChanges(): void {
    this.form.patchValue(this.eventNotification());
  }

  private getInitialFormGroup(): FormGroup<NotificationFormGroup> {
    return new FormGroup<NotificationFormGroup>({
      timing: new FormControl(null, [Validators.required]),
      number: new FormControl(null, [Validators.required]),
      unit: new FormControl(null, [Validators.required]),
      sendTime: new FormControl(null, [Validators.required]),
    });
  }

  private handleTimingChange(value: TimingOptions | null): void {
    const { number, unit } = this.form.controls;

    if (value === TimingOptions.During) {
      this.resetControls(number, unit);
      number.setValue(0);
    } else {
      this.enableControls(number, unit);
    }
  }

  private resetControls(...controls: FormControl[]): void {
    controls.forEach((control: FormControl) => {
      control.setValue(null);
      control.disable();
    });
  }

  private enableControls(...controls: FormControl[]): void {
    controls.forEach((control: FormControl) => {
      control.enable();
    });
  }

  protected readonly TimingOptions = TimingOptions;
}
